/* eslint-disable class-methods-use-this */
import * as d3 from 'd3';
import debounce from 'lodash.debounce';
import Tooltip from './Tooltip';
import { formatNumber } from './utils';

export default class ForceLayout {
  constructor({
    target, data, vars, plot = { colorPalette: [] },
  }) {
    this.target = d3.select(target);
    this.data = data;
    this.catVar = vars.cat;
    this.numVar = vars.num;
    this.colorPalette = plot.colorPalette || [];
    this.radiusRatio = plot.radiusRatio;
    this.domain = plot.domain;
    this.plotOuterWidth = null;
    this.plotOuterHeight = null;
    this.plotArea = null;
    this.scales = {
      radius: null,
      colors: null,
    };
    this.simulation = null;
    this.tick = this.tick.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.resize = debounce(this.resize, 30);
  }

  updateDimensions() {
    const width = this.target.node().parentNode.offsetWidth;
    const height = width;

    this.plotOuterWidth = width;
    this.plotOuterHeight = height;

    this.target
      .attr('viewBox', `0 0 ${this.plotOuterWidth} ${this.plotOuterHeight}`);
  }

  draw() {
    this.updateDimensions();

    this.scales.colors = d3.scaleOrdinal().range(this.colorPalette);

    this.plotArea = this.target.append('g').classed('nodes', true);
    this.tooltip = new Tooltip(this.target.node().parentNode);

    this.scales.radius = d3.scaleLinear()
      .range([0, (this.plotOuterWidth * this.radiusRatio)]);

    this.simulation = d3.forceSimulation();

    this.update();
  }

  update() {
    if (this.domain) {
      this.scales.radius.domain(this.domain);
    } else {
      this.scales.radius.domain([0, d3.max(this.data, (d) => d[this.numVar])]);
    }

    // Hardcoded
    if (this.domain) {
      this.data = this.data.map((d) => {
        const exoneraciones = d.detalle.find((v) => v.tipo_proceso === 'Contratación Directa y Exoneración');
        return {
          ...d,
          toEval: exoneraciones.monto,
        };
      });
    }

    this.nodes = this.data.map((d) => ({
      ...d,
      name: d[this.catVar],
      value: d.toEval || d[this.numVar],
      radius: d.toEval ? this.scales.radius(d.toEval) : this.scales.radius(d[this.numVar]),
    }));

    this.simulation.nodes(this.nodes);

    this.simulation
      .force('charge', d3.forceManyBody().strength(30))
      .force('collide', d3.forceCollide().radius((d) => d.radius))
      .force('center', d3.forceCenter(this.plotOuterWidth / 2, this.plotOuterHeight / 2))
      .on('tick', this.tick);

    this.simulation.restart().alpha(1);
  }

  tick() {
    const self = this;
    const circles = this.plotArea.selectAll('circle').data(this.nodes);

    circles.enter()
      .append('circle')
      .merge(circles)
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', (d) => d.radius)
      .attr('fill', (d) => this.scales.colors(d.index))
      .on('mouseover', function getValue() {
        const datum = d3.select(this).datum(); // The value comes from the circles
        self.handleMouseOver(datum);
      })
      .on('mousemove', this.handleMouseMove)
      .on('mouseleave', this.handleMouseLeave);

    circles.exit().remove();

    this.target.on('click', () => {
      const { target } = d3.event;
      if (!target.matches('circle')) {
        this.plotArea.selectAll('circle').attr('opacity', '1');
        this.handleClick();
        return;
      }
      this.plotArea.selectAll('circle').attr('opacity', '0.2');
      d3.select(target).attr('opacity', '1');
      const datum = d3.select(target).datum();
      this.handleClick(datum);
    });
  }

  resize() {
    this.updateDimensions();
    this.scales.radius.range([0, (this.plotOuterWidth * this.radiusRatio)]);
    this.update();
  }

  // eslint-disable-next-line no-unused-vars
  handleClick(datum) {}

  handleMouseOver(data) {
    const amount = `S/. ${formatNumber(data.value)}`;
    this.tooltip
      .html(`
        <p>
          <strong>Contratista: </strong>${data.name}
        <p>
        <p>
          <strong>${!data.toEval ? 'Monto' : 'Monto por contratación directa'}: </strong>${amount}
        <p>
      `)
      .show();
  }

  handleMouseMove() {
    const x = d3.event.offsetX;
    const y = d3.event.offsetY;
    this.tooltip.updatePosition(x, y);
  }

  handleMouseLeave() {
    this.tooltip.html('').hide();
  }
}
